/**
 * useFieldErrors — uniwersalny stan bledow walidacji per-pole dla formularzy.
 *
 * Cel: jedna, powtarzalna obsluga bledow inputow w calej apce. Trzyma mape
 * `pole -> komunikat`, potrafi wchlonac serwerowy `VALIDATION_ERROR` (Zod
 * `details`) i zmapowac go na komunikaty przy polach, oraz czyscic blad pola gdy
 * user zaczyna poprawiac wartosc.
 *
 * Wzorzec uzycia:
 *   const fe = useFieldErrors();
 *   ...
 *   <TextField error={fe.get('username')} onChange={(v) => { fe.clear('username'); setU(v); }} />
 *   ...
 *   onError: (err) => {
 *     // WYWOLAJ JAKO PIERWSZA INSTRUKCJE — przed kazda galezia z wczesnym `return`
 *     // (np. controlWarning.handleError, NIP_TAKEN i inne kody domenowe). Kazda
 *     // nowa odpowiedz serwera ma prawo zresetowac stan podswietlonych pol, wiec
 *     // ta funkcja musi zobaczyc KAZDY blad, nie tylko te, ktore trafiaja do
 *     // toastu ponizej. Wywolanie jej po jakims wczesnym `return` zostawia
 *     // podswietlenie z POPRZEDNIEJ proby na ekranie przy zupelnie innym bledzie.
 *     fe.applyServerError(err);
 *     if (controlWarning.handleError(err, form)) return;
 *     ...
 *     // Zwraca true gdy blad byl walidacja pol i zostal pokazany przy inputach —
 *     // uzywaj tylko do decyzji "czy pokazac generyczny toast", NIE do decyzji
 *     // "czy w ogole wywolac" (patrz wyzej).
 *   }
 *
 * Komunikaty: domyslnie bierze pierwszy surowy komunikat z serwera. Gdy chcesz
 * lokalizowane teksty, podaj `translate` — mapper `(pole, surowe[]) => tekst | null`
 * (null = zignoruj to pole, np. gdy nie masz tlumaczenia i wolisz fallback do toastu).
 */

import { useCallback, useState } from 'react';

import { extractFieldErrors } from '@/lib/errors';

export interface ApplyServerErrorOptions {
  /**
   * Mapper surowych komunikatow serwera na tekst do pokazania pod polem.
   * Zwroc `null`/`undefined` aby pominac pole (zostanie potraktowane jakby go nie
   * bylo — `applyServerError` moze wtedy zwrocic false i oddac obsluge toastowi).
   */
  translate?: (field: string, messages: string[]) => string | null | undefined;
  /**
   * Whitelist pol, ktore wolno pokazac przy inputach. Gdy podana, pola spoza listy
   * sa ignorowane (np. blad `email` w formularzu bez pola email).
   */
  fields?: readonly string[];
}

export interface FieldErrorsApi {
  /** Aktualna mapa bledow (pole -> komunikat). */
  errors: Record<string, string>;
  /** Komunikat bledu dla pola albo undefined (podstaw prosto do `error={...}`). */
  get: (field: string) => string | undefined;
  /** Ustaw blad recznie (walidacja kliencka). */
  set: (field: string, message: string) => void;
  /** Wyczysc blad pojedynczego pola (wolaj w onChange). */
  clear: (field: string) => void;
  /** Wyczysc wszystkie bledy (np. przy ponownym submit). */
  clearAll: () => void;
  /**
   * Wchlon serwerowy VALIDATION_ERROR i pokaz bledy przy polach.
   * Zwraca `true` gdy ustawil chociaz jeden blad pola (caller moze `return`),
   * `false` gdy to nie byl blad walidacji pol (caller robi fallback do toastu).
   */
  applyServerError: (err: unknown, options?: ApplyServerErrorOptions) => boolean;
}

export function useFieldErrors(): FieldErrorsApi {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const get = useCallback((field: string) => errors[field], [errors]);

  const set = useCallback((field: string, message: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  }, []);

  const clear = useCallback((field: string) => {
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setErrors({}), []);

  const applyServerError = useCallback(
    (err: unknown, options?: ApplyServerErrorOptions): boolean => {
      const fieldErrors = extractFieldErrors(err);
      const allowed = options?.fields;
      const next: Record<string, string> = {};
      for (const [field, messages] of Object.entries(fieldErrors)) {
        if (allowed && !allowed.includes(field)) continue;
        const text = options?.translate ? options.translate(field, messages) : messages[0];
        if (text) next[field] = text;
      }
      // Zastąp mapę całą — ZAWSZE, nawet gdy `next` wyszedł pusty. Pole, które
      // przestało być zgłaszane przez serwer w tej odpowiedzi, ma zgasnąć —
      // również wtedy, gdy ta odpowiedź w ogóle nie ma kształtu pól (np. inny
      // kod błędu: CONTROL_ERROR, NIP_TAKEN, zwykły 409/500). Bez tego stare
      // podświetlenie z POPRZEDNIEJ próby zostaje na ekranie i wskazuje pole,
      // które nie ma nic wspólnego z aktualnym błędem (zgłoszenie: toast mówił
      // o NIP, podświetlone zostawało "Nazwa" z wcześniejszego VALIDATION_ERROR).
      setErrors(next);
      return Object.keys(next).length > 0;
    },
    [],
  );

  return { errors, get, set, clear, clearAll, applyServerError };
}
